import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Loader2, Building2, Plus, MapPin, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LocationsPage() {
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    code: '',
    region: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocation = async () => {
    if (!newLocation.name || !newLocation.code || !newLocation.region) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/locations`, newLocation);
      toast.success('Location created successfully');
      setDialogOpen(false);
      setNewLocation({ name: '', code: '', region: '' });
      fetchLocations();
    } catch (error) {
      toast.error('Failed to create location');
    } finally {
      setCreating(false);
    }
  };

  const regionColors = {
    'North America': 'bg-blue-100 text-blue-800',
    'Europe': 'bg-purple-100 text-purple-800',
    'Asia Pacific': 'bg-emerald-100 text-emerald-800',
    'Latin America': 'bg-orange-100 text-orange-800',
    'Middle East': 'bg-amber-100 text-amber-800'
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="locations-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
              Locations
            </h1>
            <p className="text-slate-600 mt-1">Manage Showtime Pictures locations</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700" data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Location</DialogTitle>
                <DialogDescription>
                  Create a new Showtime Pictures location
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Location Name</Label>
                  <Input
                    id="name"
                    placeholder="Orlando Theme Park"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="input-location-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Location Code</Label>
                  <Input
                    id="code"
                    placeholder="ORL"
                    maxLength={5}
                    value={newLocation.code}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    data-testid="input-location-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">Region</Label>
                  <Input
                    id="region"
                    placeholder="North America"
                    value={newLocation.region}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, region: e.target.value }))}
                    data-testid="input-location-region"
                  />
                </div>
                <Button
                  onClick={handleCreateLocation}
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  disabled={creating}
                  data-testid="confirm-create-location-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Location
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Locations Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : locations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No locations yet</h3>
              <p className="text-slate-500 mb-4">Add your first location to get started</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map(loc => (
              <Card 
                key={loc.id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`location-card-${loc.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-xl bg-violet-100">
                      <Building2 className="w-6 h-6 text-violet-600" />
                    </div>
                    <Badge className={regionColors[loc.region] || 'bg-slate-100 text-slate-800'}>
                      {loc.region}
                    </Badge>
                  </div>
                  
                  <h3 className="font-semibold text-lg text-slate-900 mb-1">{loc.name}</h3>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {loc.code}
                    </span>
                    <span className="flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {loc.region}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
